import React, { useState, useEffect } from 'react';
import { getCenters, getAdminCenterAllocations, allocateCentersToAdmin } from '../../../services/supabaseService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const CenterAllocationModal = ({ user, onClose, onSuccess }) => {
  const [centers, setCenters] = useState([]);
  const [selectedCenterIds, setSelectedCenterIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCentersAndAllocations();
  }, [user]);

  const fetchCentersAndAllocations = async () => {
    try {
      setLoading(true);
      const [centersData, allocationsData] = await Promise.all([
        getCenters(),
        user?.id ? getAdminCenterAllocations(user?.id) : Promise.resolve([])
      ]);
      
      setCenters(centersData || []);
      
      // Extract center IDs from allocations
      const allocatedCenterIds = allocationsData?.map(alloc => alloc?.centerId || alloc?.centers?.id) || [];
      setSelectedCenterIds(allocatedCenterIds);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.message || 'Failed to load centers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCenter = (centerId) => {
    setSelectedCenterIds(prev => 
      prev?.includes(centerId) 
        ? prev?.filter(id => id !== centerId)
        : [...prev, centerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCenterIds?.length === centers?.length) {
      setSelectedCenterIds([]);
    } else {
      setSelectedCenterIds(centers?.map(c => c?.id));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await allocateCentersToAdmin(user?.id, selectedCenterIds);
      onSuccess?.();
    } catch (err) {
      console.error('Error allocating centers:', err);
      setError(err?.message || 'Failed to allocate centers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Allocate Centers</h2>
            <p className="text-sm text-gray-600 mt-1">{user?.fullName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Select Centers ({selectedCenterIds?.length} selected)
              </label>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                onClick={handleSelectAll}
              >
                {selectedCenterIds?.length === centers?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {loading && centers?.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                {centers?.map((center) => (
                  <div 
                    key={center?.id} 
                    className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => handleToggleCenter(center?.id)}
                  >
                    <Checkbox
                      checked={selectedCenterIds?.includes(center?.id)}
                      onChange={() => handleToggleCenter(center?.id)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{center?.name}</div>
                      <div className="text-xs text-gray-500">
                        {center?.location} {center?.city ? `• ${center?.city}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
                {centers?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Icon name="Building" size={48} className="mx-auto mb-2 text-gray-400" />
                    <p>No centers available</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading || selectedCenterIds?.length === 0}>
              {loading ? 'Saving...' : 'Save Allocations'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CenterAllocationModal;